/**
 * Client for the saved House Rules control-plane endpoints (#328, FR-19).
 *
 * A registered host can save named House Rules configurations, list + reuse them at game
 * creation (across devices), delete them, and have the create surface default to their
 * last-used mix. Identity is proven by the signed guest token ({@link ensureGuestIdentity}),
 * carried in the `x-guest-token` HEADER on the GET (no body) and in the JSON body on the
 * mutating verbs — exactly like {@link ./pack-api}. These are registered-host-only on the
 * server (guests get 403); this client is only wired for a registered host.
 *
 * Everything here is best-effort from the caller's perspective: a failed load/save/delete
 * degrades the feature (the host just doesn't get their saved rules) but NEVER blocks
 * creating or playing a game. `stampLastUsed` in particular is fire-and-forget on create.
 */

import type { HouseRules, SavedRulePreset } from '@jeopardy/shared';
import { SERVER_HTTP_URL } from '../config';
import { ensureGuestIdentity } from './auth';

/** The host's saved presets + last-used config, as returned by the GET route. */
export interface HouseRulesPrefs {
  presets: SavedRulePreset[];
  /** Undefined ⇒ the host has no remembered mix (⇒ Classic). */
  lastUsed?: HouseRules;
}

/** Raised when a save is refused because the host is already at the cap (HTTP 409). */
export class PresetCapReachedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'PresetCapReachedError';
  }
}

/**
 * Load the host's saved presets + last-used config. Returns an empty set on any failure
 * (including a guest / unregistered caller) so the create surface simply falls back to the
 * built-ins — never throws to the caller.
 */
export async function loadHouseRulesPrefs(): Promise<HouseRulesPrefs> {
  try {
    const guest = await ensureGuestIdentity();
    const res = await fetch(`${SERVER_HTTP_URL}/api/account/house-rules`, {
      headers: { 'x-guest-token': guest.guestToken },
    });
    if (!res.ok) return { presets: [] };
    const body = (await res.json()) as HouseRulesPrefs;
    return {
      presets: body.presets ?? [],
      ...(body.lastUsed ? { lastUsed: body.lastUsed } : {}),
    };
  } catch {
    return { presets: [] };
  }
}

/**
 * Save the current rules under a name. Returns the created preset. Throws a
 * {@link PresetCapReachedError} on a 409 (cap), or a plain Error on a 400 (bad name/rules)
 * or other failure — the caller surfaces the message inline.
 */
export async function savePreset(
  name: string,
  rules: HouseRules,
): Promise<SavedRulePreset> {
  const guest = await ensureGuestIdentity();
  const res = await fetch(`${SERVER_HTTP_URL}/api/account/house-rules/presets`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ name, rules, guestToken: guest.guestToken }),
  });
  if (res.status === 409) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new PresetCapReachedError(body.error ?? 'You have saved the maximum number.');
  }
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `Could not save (${res.status})`);
  }
  return (await res.json()) as SavedRulePreset;
}

/**
 * Delete one saved preset by id. Idempotent on the server (deleting an unknown id
 * succeeds). Returns the remaining presets so the caller can reconcile its list.
 */
export async function deletePreset(id: string): Promise<SavedRulePreset[]> {
  const guest = await ensureGuestIdentity();
  const res = await fetch(
    `${SERVER_HTTP_URL}/api/account/house-rules/presets/${encodeURIComponent(id)}`,
    {
      method: 'DELETE',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ guestToken: guest.guestToken }),
    },
  );
  if (!res.ok) throw new Error(`Could not delete (${res.status})`);
  const body = (await res.json().catch(() => ({}))) as { presets?: SavedRulePreset[] };
  return body.presets ?? [];
}

/**
 * Stamp the host's last-used rules after a successful create (FR-19.3). Fire-and-forget:
 * best-effort, off the create hot path, and swallows any error so a failed stamp never
 * blocks the game. `rules` of `undefined` records Classic (clears the stored value).
 */
export async function stampLastUsed(rules: HouseRules | undefined): Promise<void> {
  try {
    const guest = await ensureGuestIdentity();
    await fetch(`${SERVER_HTTP_URL}/api/account/house-rules/last-used`, {
      method: 'PUT',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ rules, guestToken: guest.guestToken }),
    });
  } catch {
    // Best-effort — a failed stamp just means the host's next visit defaults to Classic.
  }
}
